// Display formatting + the shared entitlement/tier vocabulary (id-ID).

import type { TierId } from "@/lib/types";

/** Whole-IDR price → "Gratis" | "Rp 599.000". */
export function formatIDR(amount: number): string {
  if (!amount || amount <= 0) return "Gratis";
  return `Rp ${amount.toLocaleString("id-ID")}`;
}

/** Whole-IDR amount → "Rp 0" | "Rp 599.000" (revenue/amounts — no "Gratis"). */
export function formatRupiah(amount: number): string {
  return `Rp ${amount.toLocaleString("id-ID")}`;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO-8601 → "10 Juli 2026". Falls back to the raw string when unparseable. */
export function formatDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return iso ?? "—";
  return d.toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" });
}

/** ISO-8601 → "10 Jul 2026, 14.30". */
export function formatDateTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return iso ?? "—";
  return d.toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Milliseconds remaining → ticking clock "04:59" (mm:ss) / "1:04:59" (h:mm:ss).
 * Ceils to the next whole second: "00:00" must only appear once the deadline has
 * truly passed (the ticks aren't phase-aligned with the deadline, and a payable
 * QR next to "00:00" is a contradiction), so sub-second remainders show "00:01".
 */
export function formatCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Human tier label. */
const TIER_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
};
export function tierLabel(tier: TierId | string): string {
  return TIER_LABELS[tier] ?? tier;
}

/**
 * entitlement/feature_id → human label (id-ID). Mirrors the desktop `tier.rs` vocabulary
 * (docs/LICENSING.md §2). We only DISPLAY what the API returns — never re-derive entitlements.
 */
const ENTITLEMENT_LABELS: Record<string, string> = {
  // Feature id kept for wire compat; since D34 it means "no end-card" (Free's only mark).
  "export.no_watermark": "Tanpa kartu penutup",
  no_watermark: "Tanpa kartu penutup",
  // Wire id says "unlimited"; the desktop caps a Pro export at 10 hours (license/gate.rs: never "unlimited").
  "export.unlimited": "Ekspor sampai 10 jam",
  "export.hd": "Ekspor 2K",
  "export.4k": "Ekspor 4K",
  "presets.pro": "Preset Pro",
  "module.toolkit": "Modul Toolkit",
  "module.editor": "Modul Editor",
  "module.ai": "Modul AI",
  "module.avatar": "Modul Avatar",
  "module.campaign": "Modul Campaign",
};
export function entitlementLabel(featureId: string): string {
  return ENTITLEMENT_LABELS[featureId] ?? featureId;
}

/** Map a features[] array to humanized badge labels. */
export function humanizeFeatures(features: string[] | null | undefined): string[] {
  return (features ?? []).map(entitlementLabel);
}

/** Order status → id-ID label (admin also sees the reserved states). */
const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: "Menunggu pembayaran",
  paid: "Lunas",
  failed: "Gagal",
  expired: "Kedaluwarsa",
  refunded: "Dikembalikan",
};
export function orderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status;
}

// ---- Admin vocabulary (docs/API.md §8) ----

export type PillTone = "neutral" | "pro" | "signal" | "ok" | "warn" | "crit";

export function roleLabel(role: string): string {
  return role === "admin" ? "Admin" : "Pengguna";
}

/** Order status → pill tone (admin tables). */
export function orderStatusTone(status: string): PillTone {
  switch (status) {
    case "paid":
      return "ok";
    case "pending":
      return "warn";
    case "failed":
      return "crit";
    default: // expired / refunded / unknown
      return "neutral";
  }
}

/** License state helpers (one-time model: active = not revoked; NO expiry). */
export function licenseStateLabel(revokedAt: string | null): string {
  return revokedAt ? "Dicabut" : "Aktif";
}
export function licenseStateTone(revokedAt: string | null): PillTone {
  return revokedAt ? "crit" : "ok";
}

/** Referral commission status (GET /me/referral) → id-ID label + pill tone. */
const REFERRAL_COMMISSION_STATUS_LABELS: Record<string, string> = {
  accrued: "Terkumpul",
  paid: "Dibayar",
  void: "Dibatalkan",
};
export function referralCommissionStatusLabel(status: string): string {
  return REFERRAL_COMMISSION_STATUS_LABELS[status] ?? status;
}
export function referralCommissionStatusTone(status: string): PillTone {
  switch (status) {
    case "paid":
      return "ok";
    case "accrued":
      return "warn";
    default: // void / unknown
      return "neutral";
  }
}

const percentFormat = new Intl.NumberFormat("id-ID", { style: "percent", maximumFractionDigits: 1 });

/** Ratio → "64,6%" (id-ID decimal comma, at most one decimal). */
export function formatPercent(ratio: number): string {
  return percentFormat.format(ratio);
}

/** Commission rate stored as basis points (1000 = 10%, 1250 = 12,5%) → percent display. */
export function bpsToPercentLabel(bps: number): string {
  return formatPercent(bps / 10_000);
}

const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;

/** Byte count → "850 byte" | "1,2 kB" | "3,4 MB" (decimal units, id-ID digits). */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < BYTE_UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return new Intl.NumberFormat("id-ID", {
    style: "unit",
    unit: BYTE_UNITS[unit],
    unitDisplay: "short",
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value);
}

/** Milliseconds → "1.234 ms" (rounded to whole milliseconds, id-ID grouping). */
export function formatDurationMs(ms: number): string {
  return `${Math.round(ms).toLocaleString("id-ID")} ms`;
}

/** webhook_events.status → id-ID label (open vocabulary; raw value fallback).
 *  Ordered problems-first so the derived filter dropdown surfaces them first.
 *  A null status has no label or tone: callers render NoValue ("Tanpa status")
 *  instead of a badge, so a status dot never appears without words. */
const WEBHOOK_STATUS_LABELS: Record<string, string> = {
  unmatched: "Tak cocok (order tak ditemukan)",
  amount_mismatch: "Jumlah tak cocok",
  duplicate_paid: "Duplikat (sudah lunas)",
  paid: "Lunas",
  failed: "Gagal",
  pending: "Menunggu",
};
export function webhookStatusLabel(status: string): string {
  return WEBHOOK_STATUS_LABELS[status] ?? status;
}
export function webhookStatusTone(status: string): PillTone {
  switch (status) {
    case "paid":
      return "ok";
    case "unmatched":
    case "amount_mismatch":
      return "crit";
    case "duplicate_paid":
    case "failed":
      return "warn";
    default:
      return "neutral";
  }
}

/** admin_audit_log.action → id-ID label. */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  "user.set_role": "Ubah role",
  "user.erase": "Hapus data akun",
  "license.grant": "Grant lisensi",
  "license.revoke": "Cabut lisensi",
  "license.unbind": "Lepas ikatan perangkat",
  "order.refund": "Tandai refund pesanan",
  "coupon.create": "Buat kupon",
  "coupon.update": "Ubah kupon",
  "pricing.update": "Ubah harga tier",
  "commission.paid": "Komisi dibayar",
  "commission.void": "Komisi dibatalkan",
};
export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

// ---- Coupons (marketing discount codes — see AdminCoupon in types.ts) ----

const COUPON_KIND_LABELS: Record<string, string> = {
  percent: "Persentase",
  fixed: "Nominal tetap",
};
export function couponKindLabel(kind: string): string {
  return COUPON_KIND_LABELS[kind] ?? kind;
}

/** Kind-aware select options for the coupon create form (no "all" entry — a
 *  coupon always has exactly one kind, unlike the list-filter dropdowns above). */
export function couponKindOptions(): SelectOption[] {
  return optionsFrom(COUPON_KIND_LABELS);
}

/** Discount value, formatted per kind: "20%" (percent) | "Rp 100.000" (fixed). */
export function couponValueLabel(kind: string, value: number): string {
  return kind === "percent" ? `${value}%` : formatRupiah(value);
}

/** Redemption quota: "3 / 10" or "5 / ∞" when max_redemptions is unlimited. */
export function couponQuotaLabel(redeemed: number, max: number | null): string {
  return `${redeemed} / ${max ?? "∞"}`;
}

/**
 * Validity window, as parts so a screen sets only the dates in the data face (DESIGN.md §4):
 * both bounds → dates ["16 Agu 2026, 22.06", "29 Sep 2026, 22.06"]; one bound → lead "Mulai" or
 * "Sampai" plus its date; no window → lead "Selalu aktif". Whitespace inside a date is
 * non-breaking, so a date is never split across lines.
 */
export interface CouponPeriod {
  /** Words before the dates ("Mulai", "Sampai", "Selalu aktif"); null for a two-bound window. */
  lead: string | null;
  /** The window's dates in order: none, one or both bounds. */
  dates: readonly string[];
}

export function couponPeriod(startsAt: string | null, expiresAt: string | null): CouponPeriod {
  const bound = (iso: string) => formatDateTime(iso).replace(/\s/g, " ");
  if (startsAt && expiresAt) return { lead: null, dates: [bound(startsAt), bound(expiresAt)] };
  if (startsAt) return { lead: "Mulai", dates: [bound(startsAt)] };
  if (expiresAt) return { lead: "Sampai", dates: [bound(expiresAt)] };
  return { lead: "Selalu aktif", dates: [] };
}

// ---- Filter option builders (single source: the label maps above) ----
// Deriving dropdown options from the same maps that render the table pills keeps
// the two in sync — renaming a label updates both, and adding a tier/status is
// one edit here, not one per admin page.

export interface SelectOption {
  value: string;
  label: string;
}

const ALL_STATUS: SelectOption = { value: "", label: "Semua status" };
const ALL_TIER: SelectOption = { value: "", label: "Semua tier" };

function optionsFrom(map: Record<string, string>): SelectOption[] {
  return Object.entries(map).map(([value, label]) => ({ value, label }));
}

/** Order-status filter options (full lifecycle, incl. reserved states). */
export function orderStatusOptions(): SelectOption[] {
  return [ALL_STATUS, ...optionsFrom(ORDER_STATUS_LABELS)];
}

/** Webhook-status filter options (problems first). */
export function webhookStatusOptions(): SelectOption[] {
  return [ALL_STATUS, ...optionsFrom(WEBHOOK_STATUS_LABELS)];
}

/** Tier options. paidOnly drops Free; withAll prepends a "Semua tier" entry. */
export function tierOptions(opts?: { paidOnly?: boolean; withAll?: boolean }): SelectOption[] {
  const list = Object.entries(TIER_LABELS)
    .filter(([value]) => !opts?.paidOnly || value !== "free")
    .map(([value, label]) => ({ value, label }));
  return opts?.withAll ? [ALL_TIER, ...list] : list;
}

/** License-state filter options (active | revoked). */
export function licenseStateOptions(): SelectOption[] {
  return [
    ALL_STATUS,
    { value: "active", label: licenseStateLabel(null) },
    { value: "revoked", label: licenseStateLabel("x") },
  ];
}

/** Referral-commission status filter options (accrued | paid | void). */
export function referralCommissionStatusOptions(): SelectOption[] {
  return [ALL_STATUS, ...optionsFrom(REFERRAL_COMMISSION_STATUS_LABELS)];
}
